import { gql } from '@apollo/client/core'
import {
  AcceptTeamInvitationArgs, ArcClaimDto,
  ArcTeamBlockUserArgs,
  ArcTeamDto,
  ArcTeamMemberDto,
  ArcTeamMemberGetArgs,
  ArcTeamMemberGetByIdArgs,
  ArcTeamMembershipDto,
  ArcTeamUserStatusLogDto,
  CreateDtoInterface,
  UpdateDtoInterface,
  UserAnalysisArgs,
  UserAnalysisDto
} from 'booksprout'
import { useApollo } from '../apollo/apolloModule'
import { useBaseCrud } from '../../composables/useBaseCrud'
import { arcFragments } from '../arcs/fragments'

/**
 * Replaces `ArcTeamMemberService`.
 *
 * The class took `@inject('ArcService')` for one reason: `getUserAnalysisClaims` needs the arc and
 * claim gql fragments, and it read them off `arcService.arcFragments`. Those fragments already live in
 * `modules/arcs/fragments.ts` — `ArcService.arcFragments` is a re-export field — so the factory imports
 * them directly and the dependency disappears. That is also why this slice does not have to wait for
 * 6g to split ArcService.
 *
 * `create`/`update`/`delete`/`getById` stay `throw new Error('Method not implemented.')`: team membership is
 * created by joinTeam/requestToJoin and ended by leaveTeam/removeFromTeam, and a single member is
 * read by `getMember`, which takes the team id too — never by generic CRUD. They
 * are kept because `Members.ts` fills the mixin chain's `crudService` slot with this service, and that
 * slot is typed `BaseCrudServiceInterface`. They go when stage 7 replaces the chain.
 *
 * `apiControllerPath` is 'arcTeam', not 'arcTeamMember' — members have no REST controller of their own.
 */

const crud = useBaseCrud<ArcTeamMemberDto>('arcTeam')

const create = (_model: CreateDtoInterface): Promise<ArcTeamMemberDto> => {
  throw new Error('Method not implemented.')
}

const deleteMember = (_id: number): Promise<any> => {
  throw new Error('Method not implemented.')
}

const update = (_model: UpdateDtoInterface): Promise<ArcTeamMemberDto> => {
  throw new Error('Method not implemented.')
}

/**
 * Get all members that belong to a team - does not return the same detailed info as getMember
 * @param args
 */
const get = (args: ArcTeamMemberGetArgs): Promise<ArcTeamMemberDto[]> => {
  const
    filterStatus = args.status === void 0 ? -1 : args.status,
    filterType = args.type === void 0 ? -1 : args.type

  return useApollo().query({
    variables: {
      teamId: args.teamId,
      status: filterStatus,
      type: filterType,
      needle: args.needle
    },
    query: gql`
      query GetArcTeamMembers (
        $teamId: Int!,
        $status: Int,
        $type: Int,
        $needle: String
      ) {
        arcTeamMembers (teamId: $teamId, status: $status, type: $type, needle: $needle) {
          id,
          arcTeamId,
          userEmail {
            emailAddress
          },
          user {
            id,
            image,
            name
          },
          status,
          type,
          statusReasonType,
          statusReason,
          reportedDate,
          statusDate,
          createdDate,
          activeArcCount,
          cancelledArcCount,
          reviewedArcCount,
          pastDueArcCount,
          averageRating
        }
      }
    `
  })
}

const getById = (_id: number): Promise<ArcTeamMemberDto> => {
  throw new Error('Method not implemented.')
}

const getMember = (args: ArcTeamMemberGetByIdArgs): Promise<ArcTeamMemberDto> => {
  return useApollo().query({
    variables: {
      userId: args.userId,
      teamId: args.teamId
    },
    query: gql`
      query GetArcTeamMembers (
        $userId: Int,
        $teamId: Int
      ) {
        arcTeamMember (userId: $userId, teamId: $teamId) {
          id,
          userEmail {
            emailAddress
          },
          user {
            id,
            image,
            name,
            arcClaims {
              id,
              status,
              statusDate,
              arc {
                id,
                book {
                  id,
                  title,
                  bookCover,
                  penNameType,
                  penName,
                  authorPenName {
                    id,
                    name
                  }
                }
              },
              review {
                id,
                rating,
                privateFeedback,
                review,
                sites {
                  id,
                  link,
                  status,
                  statusDate
                },
                disclaimer,
                createdDate
              }
            }
          },
          status,
          type,
          statusReasonType,
          statusReason,
          reportedDate,
          statusDate,
          createdDate,
          activeArcCount,
          cancelledArcCount,
          reviewedArcCount,
          pastDueArcCount,
          averageRating,
          arcTeamId
        }
      }
    `
  })
}

const getUserAnalysis = (args: UserAnalysisArgs): Promise<UserAnalysisDto> => {
  return useApollo().query({
    variables: {
      ...args
    },
    query: gql`
      query GetUserAnalysis (
        $userId: Int!
        $includeCancelled: Boolean,
        $includeReviews: Boolean,
        $teamId: Int,
        $teamOnly: Boolean,
      ) {
        userAnalysis (
          includeCancelled: $includeCancelled,
          includeReviews: $includeReviews,
          teamId: $teamId,
          teamOnly: $teamOnly,
          userId: $userId
        ) {
          user {
            id,
            name,
            emailAddress,
            image,
            reviewerRole
          },
          stats {
            active,
            averageRating,
            cancelled,
            complete,
            pastDue,
            reviewCount
          },
          teams {
            id,
            name,
            image,
            memberStatus,
            memberType,
            memberId,
            memberUserId,
            memberStatusReasonType,
            memberStatusReason
          }
        }
      }
    `
  })
}

const getUserAnalysisClaims = (args: UserAnalysisArgs): Promise<ArcClaimDto[]> => {
  return useApollo().query({
    variables: {
      ...args
    },
    query: gql`
      query GetUserAnalysisClaims (
        $userId: Int!
        $includeCancelled: Boolean,
        $includeReviews: Boolean,
        $teamId: Int,
        $teamOnly: Boolean,
        $take: Int,
        $skip: Int
      ) {
        userAnalysisClaims (
          includeCancelled: $includeCancelled,
          includeReviews: $includeReviews,
          teamId: $teamId,
          teamOnly: $teamOnly,
          userId: $userId,
          skip: $skip,
          take: $take
        ) {
          ...ArcClaimFragment,
          arc {
            ...ArcFragment
          }
        }
      }
      ${arcFragments.claim},
      ${arcFragments.arc},
    `
  })
}

/**
 * How many claims `getUserAnalysisClaims` finds across every page, with the same filters: the author's
 * view of one reader's review history numbers its pages from it. The page is not sent.
 */
const getUserAnalysisClaimsCount = (args: UserAnalysisArgs): Promise<number> => {
  return useApollo().query({
    variables: {
      ...args
    },
    query: gql`
      query GetUserAnalysisClaimsCount (
        $userId: Int!
        $includeCancelled: Boolean,
        $includeReviews: Boolean,
        $teamId: Int,
        $teamOnly: Boolean
      ) {
        userAnalysisClaimsCount (
          includeCancelled: $includeCancelled,
          includeReviews: $includeReviews,
          teamId: $teamId,
          teamOnly: $teamOnly,
          userId: $userId
        )
      }
    `
  })
}

const getTeams =(teamMemberId: number): Promise<ArcTeamDto[]> => {
  return useApollo().query({
    variables: {
      teamMemberId
    },
    query: gql`
      query GetArcTeams (
        $teamMemberId: Int
      ) {
        arcTeams (
          teamMemberId: $teamMemberId
        ) {
          id,
          name,
          description,
          image,
          defaultDisclaimer,
          arcTeamMembers {
            id,
            user {
              id
            }
          }
        }
      }
    `
  })
}

const setType = (memberId: number, teamId: number, type: number): Promise<ArcTeamMemberDto> => {
  return useApollo().mutate({
    variables: {
      type,
      memberId,
      teamId
    },
    mutation: gql`
      mutation SetArcTeamMemberType (
        $memberId: Int!,
        $teamId: Int!,
        $type: Int
      ) {
        setArcTeamMemberType (
          id: $memberId,
          teamId: $teamId,
          type: $type
        ) {
          id
        }
      }
    `
  })
}

const setStatus = (memberId: number, teamId: number, status: number, statusReason?: string, statusReasonType?: number, report?: boolean): Promise<ArcTeamMemberDto> => {
  return useApollo().mutate({
    variables: {
      status,
      memberId,
      teamId,
      statusReason,
      statusReasonType,
      report
    },
    mutation: gql`
      mutation SetArcTeamMemberStatus (
        $memberId: Int!,
        $teamId: Int!,
        $status: Int,
        $statusReason: String,
        $statusReasonType: Int,
        $report: Boolean
      ) {
        setArcTeamMemberStatus (
          id: $memberId,
          teamId: $teamId,
          status: $status,
          statusReason: $statusReason,
          statusReasonType: $statusReasonType,
          report: $report
        ) {
          id
        }
      }
    `
  })
}

const joinTeam = (model: ArcTeamMembershipDto): Promise<ArcTeamMemberDto> => {
  return useApollo().mutate({
    variables: {
      ...model
    },
    mutation: gql`
      mutation JoinArcTeam (
        $memberUserId: Int!,
        $teamId: Int!
        $status: Int
      ) {
        joinTeam (
          memberUserId: $memberUserId,
          teamId: $teamId,
          status: $status
        ) {
          id
        }
      }
    `
  })
}

const blockMember = (args: ArcTeamBlockUserArgs): Promise<ArcTeamMemberDto> => {
  return useApollo().mutate({
    variables: {
      ...args
    },
    mutation: gql`
      mutation BlockMember (
        $userId: Int!
        $statusReasonType: Int!,
        $arcId: Int,
        $arcTeamId: Int,
        $report: Boolean,
        $statusReason: String,
        $memberId: Int,
        $claimId: Int
      ) {
        blockMember (
          userId: $userId,
          statusReasonType: $statusReasonType,
          arcId: $arcId,
          arcTeamId: $arcTeamId,
          report: $report,
          statusReason: $statusReason,
          memberId: $memberId,
          claimId: $claimId
        ) {
          id
        }
      }
    `
  })
}

const removeFromTeam = (model: ArcTeamMembershipDto): Promise<ArcTeamMemberDto> => {
  return useApollo().mutate({
    variables: {
      teamId: model.teamId,
      memberUserId: model.memberUserId
    },
    mutation: gql`
      mutation RemoveFromArcTeam (
        $memberUserId: Int!,
        $teamId: Int!
      ) {
        removeFromTeam (
          memberUserId: $memberUserId,
          teamId: $teamId
        ) {
          id
        }
      }
    `
  })
}

const requestToJoin = (args: AcceptTeamInvitationArgs): Promise<ArcTeamMemberDto> => {
  return useApollo().mutate({
    variables: {
      ...args
    },
    mutation: gql`
      mutation RequestToJoinTeam (
        $teamId: Int!
        $userEmailId: Int
      ) {
        requestToJoinTeam (
          teamId: $teamId
          userEmailId: $userEmailId
        ) {
          id
        }
      }
    `
  })
}

const leaveTeam = (id: number): Promise<any> => {
  return useApollo().mutate({
    variables: {
      id
    },
    mutation: gql`
      mutation LeaveTeam (
        $id: Int!
      ) {
        leaveTeam (
          id: $id
        ) {
          id
        }
      }
    `
  })
}

const teamUserStatusLog = (teamId: number): Promise<ArcTeamUserStatusLogDto> => {
  return useApollo().query<ArcTeamUserStatusLogDto>({
    variables: {
      id: teamId
    },
    query: gql`
      query TeamUserStatusLog (
        $id: Int!
      ) {
        teamUserStatusLog (
          id: $id
        ) {
          date,
          type
        }
      }
    `
  }).then(model => {
    return {
      ...model,
      date: new Date(model?.date)
    }
  })
}

const acceptInvitation = (args: AcceptTeamInvitationArgs): Promise<ArcTeamMemberDto> => {
  return useApollo().mutate({
    variables: {
      ...args
    },
    mutation: gql`
      mutation AcceptTeamInvitation (
        $teamId: Int!,
        $userEmailId: Int
      ) {
        acceptTeamInvitation (
          teamId: $teamId,
          userEmailId: $userEmailId
        ) {
          id,
          type,
          status,
          userEmail {
            emailAddress
          }
        }
      }
    `
  })
}

/**
 * `globalLoader` is chained by the list mixin, so it hands back this bag rather than apollo's.
 */
const service = {
  ...crud,

  get,
  getById,
  getMember,
  getTeams,
  getUserAnalysis,
  getUserAnalysisClaims,
  getUserAnalysisClaimsCount,
  setType,
  setStatus,
  joinTeam,
  removeFromTeam,
  blockMember,
  requestToJoin,
  leaveTeam,
  teamUserStatusLog,
  acceptInvitation,
  create,
  update,
  delete: deleteMember,

  globalLoader (enable: boolean) {
    crud.setGlobalLoader(enable)
    return service
  }
}

export function arcTeamMemberService () {
  return service
}
